import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryZone } from '../types/delivery-zone.type';
import { UpdateDeliveryZoneRequestDTO } from '../types/dtos/update-delivery-zone.dto';
import { ADMIN_DELIVERY_ZONES_API_PATHS } from '../constants/delivery-zones-api-paths.constants';

type UpdateDeliveryZoneParams = {
  deliveryZoneId: string;
  deliveryZoneData: UpdateDeliveryZoneRequestDTO;
};

type UseUpdateDeliveryZoneResult = {
  updateDeliveryZone: (params: UpdateDeliveryZoneParams) => Promise<DeliveryZone | null>;
  isSubmitting: boolean;
};

export function useUpdateDeliveryZone(): UseUpdateDeliveryZoneResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateDeliveryZone = async ({
    deliveryZoneId,
    deliveryZoneData,
  }: UpdateDeliveryZoneParams): Promise<DeliveryZone | null> => {
    setIsSubmitting(true);

    try {
      const updatedDeliveryZone = await HttpClient.patch<DeliveryZone, UpdateDeliveryZoneRequestDTO>(
        ADMIN_DELIVERY_ZONES_API_PATHS.UPDATE_DELIVERY_ZONE(deliveryZoneId),
        deliveryZoneData
      );

      enqueueSnackbar('Zone de livraison mise à jour avec succès', { variant: 'success' });
      return updatedDeliveryZone;
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la mise à jour de la zone de livraison'
        ),
        { variant: 'error' }
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { updateDeliveryZone, isSubmitting };
}
